import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolvePhotoUrl } from "@/lib/photos";

/**
 * Dashboard-level Boost Home Spotlight banner.
 * Placed between stats and listing list.
 * Logic:
 *  - 1 listing    => direct checkout for that listing
 *  - 2+ listings  => modal picker
 *  - 0 listings   => CTA disabled, guides user to create listing first
 *  - sold-out     => CTA becomes "Iscriviti alla waitlist"
 */
export default function BoostBanner({ listings = [] }) {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [waitlistMe, setWaitlistMe] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/featured/status").then((r) => { if (mounted) setStatus(r.data); }).catch(() => {});
    api.get("/featured/waitlist/me").then((r) => { if (mounted) setWaitlistMe(r.data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Listings eligible for boost: not already featured AND status active
  const eligible = useMemo(
    () => listings.filter((l) => l.status === "active" && !l.featured_slot),
    [listings]
  );

  const activeCount = status?.active ?? 0;
  const totalSlots = status?.total_slots ?? 8;
  const available = status?.available ?? totalSlots;
  const price = status?.price_eur ?? 9.9;
  const duration = status?.duration_days ?? 30;
  const soldOut = status && available === 0;
  const waitlistIn = !!waitlistMe?.in_waitlist;

  const startCheckout = (listingId) => {
    nav(`/checkout?featured=1&listing_id=${encodeURIComponent(listingId)}`);
  };

  const handleCtaClick = () => {
    if (soldOut) return handleWaitlist();
    if (eligible.length === 0) {
      toast.error("Crea prima un annuncio attivo per poterlo mettere in home.");
      return;
    }
    if (eligible.length === 1) return startCheckout(eligible[0].id);
    setPickerOpen(true);
  };

  const handleWaitlist = async () => {
    setBusy(true);
    try {
      if (waitlistIn) {
        await api.delete("/featured/waitlist");
        toast.success("Rimosso dalla waitlist");
        setWaitlistMe({ in_waitlist: false, entry: null });
      } else {
        await api.post("/featured/waitlist");
        toast.success("Sei in waitlist. Ti avvisiamo quando si libera uno slot.");
        setWaitlistMe({ in_waitlist: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  // Bar fill % for visual slot occupancy
  const fillPct = Math.min(100, Math.round((activeCount / totalSlots) * 100));

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-[#FFBE00]/60 bg-gradient-to-br from-[#1a1200] via-black to-[#1a0012] p-6 md:p-8 mb-10"
      data-testid="boost-banner"
    >
      {/* decorative glow */}
      <div
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #FFBE00 0%, transparent 70%)" }}
      />
      <div className="relative flex flex-col lg:flex-row items-start gap-6 lg:gap-10">
        {/* Left: info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#FFBE00] text-black text-[10px] font-bold uppercase tracking-wider">
              <i className="fas fa-bolt"></i> Home Spotlight
            </span>
            {soldOut ? (
              <span className="px-2 py-1 rounded border border-red-500 text-red-400 text-[10px] font-bold uppercase tracking-wider">
                Sold out
              </span>
            ) : (
              <span className="px-2 py-1 rounded border border-[#22C55E] text-[#22C55E] text-[10px] font-bold uppercase tracking-wider" data-testid="boost-slots-badge">
                {available}/{totalSlots} slot liberi
              </span>
            )}
          </div>
          <h2 className="font-heading font-black text-2xl md:text-3xl leading-tight">
            Metti il tuo annuncio <span className="text-[#FFBE00]">in home page</span>.
          </h2>
          <p className="text-sm md:text-base text-zinc-300 mt-2 max-w-xl">
            Solo <strong className="text-white">8 posti disponibili</strong> in tutta HostMarket. Massima visibilità su ogni sessione,
            attivo per <strong className="text-white">{duration} giorni</strong> a
            <strong className="text-[#FFBE00]"> €{price.toFixed(2).replace(".", ",")}</strong>.
          </p>
          <ul className="mt-4 space-y-1.5 text-sm text-zinc-400">
            <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Visibilità prioritaria sopra tutti gli altri annunci</li>
            <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Waitlist se sono pieni: scatta l'email appena si libera uno slot</li>
            <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Reminder automatico via email a 1 giorno dalla scadenza</li>
          </ul>

          {/* Slot occupancy bar */}
          <div className="mt-5">
            <div className="flex justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
              <span>{activeCount}/{totalSlots} occupati</span>
              <span>Aggiornato in tempo reale</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${fillPct}%`,
                  background: soldOut
                    ? "linear-gradient(90deg, #E619A8 0%, #FFBE00 100%)"
                    : "linear-gradient(90deg, #FFBE00 0%, #22C55E 100%)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Right: CTA */}
        <div className="w-full lg:w-auto lg:min-w-[260px] flex flex-col gap-3">
          {!soldOut ? (
            <button
              type="button"
              onClick={handleCtaClick}
              disabled={busy}
              className="px-6 py-4 rounded-md bg-[#FFBE00] text-black font-black uppercase tracking-wider text-sm hover:bg-[#E619A8] hover:text-white transition-all disabled:opacity-60 shadow-[0_0_20px_rgba(255,190,0,0.3)]"
              data-testid="boost-cta"
            >
              <i className="fas fa-bolt mr-2"></i>
              {eligible.length === 0 ? "Serve un annuncio attivo" : eligible.length === 1 ? "Metti in home" : "Scegli annuncio da boostare"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleWaitlist}
              disabled={busy}
              className={`px-6 py-4 rounded-md font-black uppercase tracking-wider text-sm transition-all disabled:opacity-60 ${
                waitlistIn
                  ? "border border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-400"
                  : "bg-[#E619A8] text-white hover:bg-[#FFBE00] hover:text-black"
              }`}
              data-testid="boost-waitlist-cta"
            >
              <i className={`fas ${waitlistIn ? "fa-bell-slash" : "fa-bell"} mr-2`}></i>
              {waitlistIn ? "Esci dalla waitlist" : "Iscriviti alla waitlist"}
            </button>
          )}
          <div className="text-xs text-zinc-500 text-center">
            {soldOut
              ? "Appena si libera uno slot ricevi un'email con il link per prenotarlo"
              : `Pagamento sicuro · Stripe · ${duration} giorni attivi`}
          </div>
        </div>
      </div>

      {/* Modal picker for 2+ listings */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setPickerOpen(false)}
          data-testid="boost-picker-modal"
        >
          <div
            className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-[#0A0A0A] p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-black text-xl">Quale annuncio metti in home?</h3>
              <button onClick={() => setPickerOpen(false)} className="text-zinc-500 hover:text-white text-xl leading-none" data-testid="boost-picker-close">&times;</button>
            </div>
            <p className="text-sm text-zinc-400 mb-5">Seleziona l'annuncio da boostare per {duration} giorni.</p>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {eligible.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => startCheckout(l.id)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-md border border-zinc-800 hover:border-[#FFBE00] hover:bg-[#FFBE00]/5 transition-all"
                  data-testid={`boost-pick-${l.id}`}
                >
                  {l.photos?.[0] ? (
                    <img src={resolvePhotoUrl(l.photos[0])} alt="" className="w-12 h-12 object-cover rounded-md border border-zinc-800" />
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-zinc-900 flex items-center justify-center text-zinc-600">
                      <i className="fas fa-image"></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{l.title}</div>
                    <div className="text-[11px] text-zinc-500">{l.category_name} · {l.city || "—"}</div>
                  </div>
                  <i className="fas fa-chevron-right text-[#FFBE00]"></i>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
